import { ENEMIES } from './enemies';
import { SIM_HZ } from './tuning';

/** Lead time of the INTERCEPT warning (GDD §8.1): 1.0 s before the enemy enters the screen. */
export const INTERCEPT_LEAD_S = 1.0;
export const INTERCEPT_LEAD_TICKS = Math.round(INTERCEPT_LEAD_S * SIM_HZ);

/** How an enemy announces its entry (GDD §8.1). */
export type Announce = 'intercept' | 'own-telegraph' | 'none';

export interface EnemyEntry {
  announce: Announce;
  /** Speed to the left on top of the scroll (u/s): the enemy enters the screen at scroll + extraSpeed. */
  extraSpeed: number;
}

/**
 * Entry of each enemy that can be announced (issue #83). Fast enemies (a flight speed well above the
 * scroll, or entering from behind) use INTERCEPT; the others announce themselves or need nothing. Only the
 * speed matters here: each enemy implements its own behaviour in src/core (EN-03 with #85).
 * - EN-01 enters at scroll + 1 u/s: nearly the scroll, so no INTERCEPT (threshold: more than 2 u/s above it).
 * - EN-11 uses its bubbles (#87) and EN-12 follows its cable, so neither is announced here.
 */
export const ENEMY_ENTRY: Readonly<Record<string, EnemyEntry>> = Object.freeze({
  'EN-01': { announce: 'none', extraSpeed: 1 },
  // EN-23 (#106): the flock is one wide threat, announced once at the height of its leader (GDD §8.3).
  'EN-23': { announce: 'intercept', extraSpeed: ENEMIES['EN-23'].relativeSpeed },
  'EN-02': { announce: 'none', extraSpeed: 0 },
  'EN-03': { announce: 'intercept', extraSpeed: ENEMIES['EN-03'].extraSpeed },
  'EN-04': { announce: 'none', extraSpeed: 0 },
  'EN-11': { announce: 'own-telegraph', extraSpeed: 0 },
  'EN-12': { announce: 'none', extraSpeed: 0 },
  // EN-31 (#116) flies slower than the scroll (scroll minus its world speed): nothing to announce.
  'EN-31': { announce: 'none', extraSpeed: -ENEMIES['EN-31'].worldSpeed },
  // EN-22 (#105) is never announced itself: the EN-03 it calls is.
  'EN-22': { announce: 'none', extraSpeed: 0 },
  // EN-21 (#104) is fixed in the world and announces each rotation with its own beacon.
  'EN-21': { announce: 'own-telegraph', extraSpeed: 0 },
});

/**
 * Entry of one enemy of the level: the type's, except Veneno's motor paraglider (an EN-04 with
 * `params.motor = 1`, issue #98), which flies above the threshold and is announced with INTERCEPT.
 */
export function enemyEntry(type: string, params?: Readonly<Record<string, number>>): EnemyEntry | undefined {
  if (type === 'EN-04' && params?.motor === 1) return { announce: 'intercept', extraSpeed: ENEMIES['EN-04'].motorSpeed };
  return ENEMY_ENTRY[type];
}

/** Threshold of the rule: an enemy faster than the scroll by more than this is announced (u/s). */
export const INTERCEPT_SPEED_THRESHOLD = 2;
